import {
  DynamicFooter,
  DynamicHeader,
} from "@/components/DynamicComponentRenderer";
import { MetaHead } from "@/components/MetaHead";
import { useConfig } from "@/context/ConfigContext";
import { getInitialSeoForPage } from "@/lib/serverSeoConfig";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { RiArrowRightSLine } from "react-icons/ri";

export default function BlogList({ initialSeo }) {
  const router = useRouter();
  const { config, configData, isConfigReady } = useConfig();
  const imageBaseUrl = config?.api?.imageUrl || "";
  const teamId = configData?.team_id;

  // State untuk blog
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsError, setBlogsError] = useState(null);
  const [pagination, setPagination] = useState(null);

  // State untuk pagination
  const [page, setPage] = useState(1);
  const perPage = 12;

  // State untuk blog categories
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Ambil blog categories
  useEffect(() => {
    if (!teamId) return;

    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await fetch(`/api/blog-category?team_id=${teamId}`);
        if (!res.ok) {
          throw new Error("Gagal memuat kategori blog");
        }

        const data = await res.json();
        let items = [];

        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          items = data.data.data;
        } else if (Array.isArray(data?.categories)) {
          items = data.categories;
        }

        setCategories(items);
      } catch (error) {
        console.error("Error fetching blog categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [teamId]);

  // Ambil data blog dari API
  useEffect(() => {
    if (!isConfigReady) return;

    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        setBlogsLoading(true);
        setBlogsError(null);

        const params = new URLSearchParams({
          page: page.toString(),
          per_page: perPage.toString(),
        });

        if (selectedCategory) {
          params.append("category_id", selectedCategory);
        }

        const res = await fetch(`/api/blog?${params}`);
        if (!res.ok) {
          throw new Error("Gagal memuat blog");
        }

        const data = await res.json();

        // Normalisasi data sesuai struktur API
        let items = [];
        let paginationData = null;

        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          items = data.data.data;
          paginationData = data.data;
        } else if (Array.isArray(data?.blogs)) {
          items = data.blogs;
        }

        if (data?.meta || data?.pagination) {
          paginationData = data.meta || data.pagination;
        }

        if (!isMounted) return;
        setBlogs(items);
        setPagination(paginationData);
      } catch (error) {
        if (!isMounted) return;
        setBlogsError(error?.message || "Terjadi kesalahan saat memuat blog");
      } finally {
        if (!isMounted) return;
        setBlogsLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false;
    };
  }, [isConfigReady, page, selectedCategory]);

  const totalPage = useMemo(() => {
    if (pagination?.last_page) return pagination.last_page;
    if (pagination?.total_pages) return pagination.total_pages;
    return Math.ceil((blogs?.length || 0) / perPage) || 1;
  }, [pagination, blogs, perPage]);

  // Helper untuk mendapatkan image URL
  const getBlogImageUrl = (blog) => {
    const imageSrc =
      blog?.image ||
      blog?.thumbnail ||
      (Array.isArray(blog?.images) ? blog.images[0] : blog?.images) ||
      "/images/article/article-1.webp";

    if (imageSrc.startsWith("http://") || imageSrc.startsWith("https://")) {
      return imageSrc;
    }

    if (imageSrc.startsWith("/")) {
      return imageSrc;
    }

    if (imageBaseUrl) {
      const baseUrl = imageBaseUrl.replace(/\/$/, "");
      const imagePath = imageSrc.replace(/^\//, "");
      return `${baseUrl}/${imagePath}`;
    }

    return imageSrc;
  };

  return (
    <>
      <MetaHead
        pagePath="/blog"
        title={initialSeo?.title || "Blog - Tips dan Trik Menarik"}
        description={initialSeo?.description}
        ogImage={initialSeo?.ogImage}
        ogUrl={initialSeo?.ogUrl}
      />
      <DynamicHeader />
      <div className="overflow-x-hidden">
        <div className="px-4 py-8 sm:py-12 md:py-20 lg:pb-24 lg:pt-12 sm:px-6 md:px-12 lg:px-24 space-y-16 md:space-y-24 mb-16 md:mb-0">
          <div className="space-y-4 md:space-y-6">
            {/* Breadcrumb */}
            <nav
              className="flex flex-wrap items-center gap-2 text-sm md:text-base"
              aria-label="Breadcrumb"
            >
              <Link href="/" className="font-semibold hover:text-nevada-950">
                Home
              </Link>
              <RiArrowRightSLine />
              <div className="font-semibold text-nevada-950">Blog</div>
            </nav>
            {/* End Breadcrumb */}

            <div className="flex flex-col justify-center items-center gap-4">
              <div className="text-3xl font-semibold">Blog</div>
              <div className="text-nevada-600 text-lg">
                Tips dan Trik Menarik!
              </div>
            </div>

            {/* Category Filter */}
            {categoriesLoading ? (
              <div className="flex flex-wrap gap-2">
                {[...Array(5)].map((_, idx) => (
                  <div
                    key={idx}
                    className="bg-nevada-200 rounded-full px-4 py-2 w-24 h-10 animate-pulse"
                  />
                ))}
              </div>
            ) : categories && categories.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`rounded-full px-4 py-2 text-sm border transition ${
                    !selectedCategory
                      ? "bg-nevada-500 text-white border-nevada-500"
                      : "bg-white text-nevada-950 border-nevada-100 hover:border-nevada-950"
                  }`}
                >
                  Semua
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id || category.slug}
                    onClick={() =>
                      setSelectedCategory(category.id || category.slug)
                    }
                    className={`rounded-full px-4 py-2 text-sm border transition ${
                      selectedCategory === (category.id || category.slug)
                        ? "bg-nevada-950 text-white border-nevada-950"
                        : "bg-white text-nevada-950 border-nevada-100 hover:border-nevada-950"
                    }`}
                  >
                    {category.name || category.title || "Kategori"}
                  </button>
                ))}
              </div>
            ) : null}

            {/* Blog List */}
            {blogsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {[...Array(8)].map((_, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl border border-nevada-100 animate-pulse"
                  >
                    <div className="w-full h-52 sm:h-48 bg-nevada-200 rounded-t-2xl" />
                    <div className="p-4 sm:p-6 space-y-3">
                      <div className="h-3 bg-nevada-200 rounded w-1/2" />
                      <div className="h-4 bg-nevada-200 rounded w-3/4" />
                      <div className="h-3 bg-nevada-200 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : blogsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-600 text-sm">
                  Gagal memuat blog: {blogsError}
                </p>
              </div>
            ) : blogs && blogs.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {blogs.map((post, idx) => {
                    const imageSrc = getBlogImageUrl(post);

                    const category =
                      post?.categories?.[0]?.name ||
                      post?.category?.name ||
                      post?.tags?.[0]?.tags ||
                      post?.category ||
                      "Blog";

                    let dateText = "";
                    const rawDate =
                      post?.published_at ||
                      post?.created_at ||
                      post?.date ||
                      "";
                    if (rawDate) {
                      const d = new Date(rawDate);
                      if (!isNaN(d.getTime())) {
                        dateText = d.toLocaleDateString("id-ID", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        });
                      } else {
                        dateText = rawDate;
                      }
                    }

                    const title =
                      post?.title || post?.name || "Judul artikel menarik";

                    const slug = post?.slug || post?.id || idx;

                    // Ambil ringkasan dari konten HTML
                    const rawContent =
                      post?.excerpt ||
                      post?.summary ||
                      post?.description ||
                      post?.content ||
                      "";
                    const plainContent =
                      typeof rawContent === "string"
                        ? rawContent
                            .replace(/<[^>]+>/g, " ")
                            .replace(/\s+/g, " ")
                            .trim()
                        : "";
                    const excerpt =
                      plainContent && plainContent.length > 140
                        ? `${plainContent.slice(0, 140)}...`
                        : plainContent ||
                          "Baca selengkapnya untuk mendapatkan tips dan informasi menarik.";

                    return (
                      <Link
                        key={post?.id || post?.slug || idx}
                        href={`/blog/${slug}`}
                        className="bg-white rounded-2xl border border-nevada-100 hover:border-nevada-950 transition"
                      >
                        <div className="w-full h-52 sm:h-48 relative rounded-t-2xl overflow-hidden">
                          <Image
                            src={imageSrc}
                            alt={title}
                            fill
                            className="object-cover w-full h-full"
                            sizes="(max-width: 768px) 100vw, 25vw"
                            onError={(e) => {
                              e.target.src = "/images/article/article-1.webp";
                            }}
                          />
                        </div>
                        <div className="p-4 sm:p-6 space-y-2 sm:space-y-4">
                          <div className="flex text-xs sm:text-base">
                            <b>{category}</b>
                            {dateText && (
                              <span className="ml-1">- {dateText}</span>
                            )}
                          </div>
                          <h3 className="text-base sm:text-lg font-semibold">
                            {title}
                          </h3>
                          <div className="text-nevada-600 text-xs sm:text-base">
                            {excerpt}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPage > 1 && (
                  <div className="flex justify-center items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 rounded-lg border border-nevada-100 hover:border-nevada-950 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPage) }).map(
                        (_, idx) => {
                          let pageNum;
                          if (totalPage <= 5) {
                            pageNum = idx + 1;
                          } else if (page <= 3) {
                            pageNum = idx + 1;
                          } else if (page >= totalPage - 2) {
                            pageNum = totalPage - 4 + idx;
                          } else {
                            pageNum = page - 2 + idx;
                          }

                          return (
                            <button
                              key={idx}
                              onClick={() => setPage(pageNum)}
                              className={`px-4 py-2 rounded-lg border ${
                                page === pageNum
                                  ? "bg-nevada-950 text-white border-nevada-950"
                                  : "border-nevada-100 hover:border-nevada-950"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                      disabled={page === totalPage}
                      className="px-4 py-2 rounded-lg border border-nevada-100 hover:border-nevada-950 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Selanjutnya
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-nevada-50 border border-nevada-200 rounded-lg p-4 text-center">
                <p className="text-nevada-600 text-sm">
                  Belum ada postingan blog tersedia.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <DynamicFooter />
    </>
  );
}

export async function getServerSideProps(context) {
  const initialSeo = await getInitialSeoForPage(context.req, "/blog", {
    title: "Blog - Tips dan Trik Menarik",
  });
  return { props: { initialSeo: initialSeo || null } };
}
